import { useEffect, useState } from 'react';
import type { AgentRow, Api } from '../api';
import { agentTitle } from '../api';
import { PERM, useCan } from '../access';
import type { T } from '../i18n';
import { ReadOnlyNote, SaveBar, Section, useDraft } from '../ui';
import { useSpecSave, type EditableSpec } from './AgentSettings';

type Target = { kind: 'agent'; agentId: string } | { kind: 'phone'; e164: string } | null;
type Route = { id: string; topic: string; description: string; target: Target };
type Row = { id: string; topic: string; description: string; kind: 'self' | 'agent' | 'phone'; agentId: string; e164: string };

/** A stable id from the topic (letters and digits only); any script → a numbered id when nothing is left. */
const idFor = (topic: string, taken: Set<string>, i: number): string => {
  const base = topic.normalize('NFKD').replace(/[̀-ͯ]/gu, '').toLowerCase().replace(/[^a-z0-9]+/gu, '_').replace(/^_+|_+$/gu, '').slice(0, 40);
  let id = /^[a-z]/u.test(base) ? base : `route_${i + 1}`;
  for (let n = 2; taken.has(id); n += 1) id = `${id.replace(/_\d+$/u, '')}_${n}`;
  taken.add(id);
  return id;
};

const toRow = (r: Route): Row => ({
  id: r.id, topic: r.topic, description: r.description,
  kind: r.target === null ? 'self' : r.target.kind, agentId: r.target?.kind === 'agent' ? r.target.agentId : '', e164: r.target?.kind === 'phone' ? r.target.e164 : '',
});
export const toRoutes = (rows: Row[]): Route[] => {
  const taken = new Set<string>();
  return rows
    .filter((r) => r.topic.trim())
    .map((r, i) => ({
      id: r.id || idFor(r.topic, taken, i),
      topic: r.topic.trim(),
      description: r.description.trim() || r.topic.trim(),
      target: r.kind === 'agent' && r.agentId ? { kind: 'agent', agentId: r.agentId } : r.kind === 'phone' && r.e164.trim() ? { kind: 'phone', e164: `+${r.e164.replace(/\D/gu, '')}` } : null,
    }));
};

/**
 * ADR-031 routing tab: the topics callers ask about (in the business's words) and where each goes —
 * another agent (same call), a phone (transfer), or this agent takes a message.
 */
export const RoutingPanel = ({ api, t, agentId, spec, onSaved }: { api: Api; t: T; agentId: string; spec: EditableSpec; onSaved: () => void }) => {
  // ADR-071: without the agents permission the routes are shown read-only.
  const canEdit = useCan()(PERM.agents);
  const routes = (spec.routes as Route[] | undefined) ?? [];
  const d = useDraft<Row[]>(routes.map(toRow));
  const s = useSpecSave(api, agentId, onSaved, t);
  // null = not loaded yet (or failed): no target is called unreachable on a guess.
  const [agents, setAgents] = useState<AgentRow[] | null>(null);
  useEffect(() => void api.agents().then((r) => setAgents(r.items.filter((a) => a.id !== agentId))).catch(() => undefined), [api, agentId]);
  const rows = d.value;
  const set = (i: number, p: Partial<Row>) => d.set(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const hasCallback = ((spec.globalIntents as Array<{ action: string }> | undefined) ?? []).some((g) => g.action === 'schedule_callback');
  const badPhone = rows.some((r) => r.kind === 'phone' && !/^\+?[1-9][\d\s()-]{7,20}$/u.test(r.e164.trim()));
  // ADR-031: on a call, a topic handed to an agent that cannot take calls (not published) becomes "take a message".
  const unreachable = (r: Row) => r.kind === 'agent' && !!r.agentId && agents !== null && !agents.some((a) => a.id === r.agentId && a.publishedPackageId);
  const connects = (r: Row) => (r.kind === 'agent' ? !!r.agentId && !unreachable(r) : r.kind === 'phone' && !!r.e164.trim());
  const topics = rows.filter((r) => r.topic.trim());
  const connected = topics.filter(connects).length;

  return (
    <div className="settings">
      <fieldset disabled={!canEdit} className="readonly-fieldset">
        <ReadOnlyNote t={t} need={PERM.agents} />
        <Section title={t('routing.title')} description={t('routing.hint')}>
          {rows.length === 0 && <p className="muted">{t('routing.empty')}</p>}
          {topics.length > 0 && <p className="muted small">{t('routing.summary', { connected, total: topics.length, message: topics.length - connected })}</p>}
          {topics.length > 0 && connected === 0 && <p className="banner warn small">{t('routing.allMessage')}</p>}
          <div className="route-list">
            {rows.map((r, i) => (
              <div key={i} className="route-row">
                <div className="route-main">
                  <input value={r.topic} placeholder={t('routing.topic.placeholder')} aria-label={t('routing.topic')} onChange={(e) => set(i, { topic: e.target.value })} />
                  <textarea rows={2} value={r.description} placeholder={t('routing.description.placeholder')} aria-label={t('routing.description')} onChange={(e) => set(i, { description: e.target.value })} />
                </div>
                <div className="route-target">
                  <select value={r.kind === 'agent' ? `agent:${r.agentId}` : r.kind} aria-label={t('routing.target')} onChange={(e) => {
                    const v = e.target.value;
                    set(i, v.startsWith('agent:') ? { kind: 'agent', agentId: v.slice(6) } : { kind: v as 'self' | 'phone' });
                  }}>
                    <option value="self">{t('routing.target.self')}</option>
                    {(agents ?? []).map((a) => <option key={a.id} value={`agent:${a.id}`}>{t('routing.target.agent', { name: agentTitle(a) })}{a.publishedPackageId ? '' : ` (${t('lines.notPublished')})`}</option>)}
                    <option value="phone">{t('routing.target.phone')}</option>
                  </select>
                  {r.kind === 'phone' && <input className="mono" value={r.e164} placeholder={t('routing.phone.placeholder')} inputMode="tel" dir="ltr" aria-label={t('routing.phone')} onChange={(e) => set(i, { e164: e.target.value })} />}
                  {canEdit && <button type="button" className="danger" onClick={() => d.set(rows.filter((_, j) => j !== i))}>{t('settings.remove')}</button>}
                </div>
                {unreachable(r) && <p className="banner warn small" style={{ gridColumn: '1 / -1' }}>{t('routing.notPublished')}</p>}
              </div>
            ))}
          </div>
          {canEdit && <button type="button" className="secondary" disabled={rows.length >= 20} onClick={() => d.set([...rows, { id: '', topic: '', description: '', kind: 'self', agentId: '', e164: '' }])}>{t('routing.add')}</button>}
          {topics.some((r) => !connects(r)) && !hasCallback &&<p className="banner warn small">{t('routing.needCallback')}</p>}
          {badPhone && <p className="banner error small">{t('routing.badPhone')}</p>}
          <p className="hint">{t('routing.phoneNote')}</p>
        </Section>
      </fieldset>
      <SaveBar t={t} need={PERM.agents} dirty={d.dirty} saving={s.saving} error={s.error} onReset={d.reset} onSave={() => !badPhone && void s.save({ routes: toRoutes(rows) }, d.commit)} />
    </div>
  );
};
